import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { clientAnalyticsService } from '../../../services/clientAnalyticsService';

const EmailCommunicationLog = ({ dateRange, loading: parentLoading }) => {
  const [logs, setLogs] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    clientId: '',
    reportType: 'all',
    status: 'all'
  });
  const [clients, setClients] = useState([]);

  useEffect(() => {
    if (dateRange?.startDate && dateRange?.endDate) {
      loadEmailLogs();
    }
  }, [dateRange, filters]);

  const loadEmailLogs = async () => {
    setLoading(true);
    try {
      const filterParams = {
        startDate: dateRange?.startDate,
        endDate: dateRange?.endDate,
        clientId: filters?.clientId || undefined,
        reportType: filters?.reportType,
        status: filters?.status
      };

      const [logsData, metricsData] = await Promise.all([
        clientAnalyticsService?.getClientEmailCommunicationLogs(filterParams),
        clientAnalyticsService?.getClientEmailEngagementMetrics(filterParams)
      ]);

      setLogs(logsData || []);
      setMetrics(metricsData);

      // Extract unique clients for filter dropdown
      const uniqueClients = {};
      logsData?.forEach(log => {
        if (log?.client?.id && !uniqueClients?.[log?.client?.id]) {
          uniqueClients[log?.client?.id] = {
            id: log?.client?.id,
            name: log?.client?.nombre
          };
        }
      });
      setClients(Object.values(uniqueClients));
    } catch (error) {
      console.error('Error loading email logs:', error);
      setLogs([]);
      setMetrics(null);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'sent':
        return 'text-green-600 bg-green-100';
      case 'failed':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getReportTypeLabel = (type) => {
    const labels = {
      financial_report: 'Reporte Financiero',
      client_analytics: 'Análisis de Cliente',
      threshold_alerts: 'Alertas de Umbral'
    };
    return labels?.[type] || type;
  };

  const getReportTypeIcon = (type) => {
    const icons = {
      financial_report: 'DollarSign',
      client_analytics: 'BarChart3',
      threshold_alerts: 'Bell'
    };
    return icons?.[type] || 'Mail';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleString('es-MX', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isLoading = loading || parentLoading;

  return (
    <div className="bg-card border border-border rounded-lg shadow-elevation-2 p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Icon name="Mail" size={24} className="text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-heading font-bold text-foreground">
              Registro de Comunicación por Email
            </h2>
            <p className="text-sm text-muted-foreground">
              Reportes programados enviados y métricas de engagement
            </p>
          </div>
        </div>
      </div>
      {/* Engagement Metrics Summary */}
      {metrics?.overall && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="p-4 bg-background border border-border rounded-lg">
            <div className="flex items-center gap-2 mb-1">
              <Icon name="Send" size={16} className="text-blue-600" />
              <span className="text-sm text-muted-foreground">Enviados</span>
            </div>
            <p className="text-2xl font-bold text-foreground">{metrics?.overall?.totalSent}</p>
          </div>
          <div className="p-4 bg-background border border-border rounded-lg">
            <div className="flex items-center gap-2 mb-1">
              <Icon name="Eye" size={16} className="text-green-600" />
              <span className="text-sm text-muted-foreground">Tasa de Apertura</span>
            </div>
            <p className="text-2xl font-bold text-green-600">
              {metrics?.overall?.openRate?.toFixed(1)}%
            </p>
          </div>
          <div className="p-4 bg-background border border-border rounded-lg">
            <div className="flex items-center gap-2 mb-1">
              <Icon name="MousePointerClick" size={16} className="text-purple-600" />
              <span className="text-sm text-muted-foreground">Tasa de Clics</span>
            </div>
            <p className="text-2xl font-bold text-purple-600">
              {metrics?.overall?.clickRate?.toFixed(1)}%
            </p>
          </div>
          <div className="p-4 bg-background border border-border rounded-lg">
            <div className="flex items-center gap-2 mb-1">
              <Icon name="CheckCircle" size={16} className="text-blue-600" />
              <span className="text-sm text-muted-foreground">Tasa de Entrega</span>
            </div>
            <p className="text-2xl font-bold text-blue-600">
              {metrics?.overall?.deliveryRate?.toFixed(1)}%
            </p>
          </div>
        </div>
      )}
      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Cliente
          </label>
          <select
            value={filters?.clientId}
            onChange={(e) => setFilters(prev => ({ ...prev, clientId: e?.target?.value }))}
            className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="">Todos los clientes</option>
            {clients?.map(client => (
              <option key={client?.id} value={client?.id}>
                {client?.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Tipo de Reporte
          </label>
          <select
            value={filters?.reportType}
            onChange={(e) => setFilters(prev => ({ ...prev, reportType: e?.target?.value }))}
            className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="all">Todos los tipos</option>
            <option value="financial_report">Reporte Financiero</option>
            <option value="client_analytics">Análisis de Cliente</option>
            <option value="threshold_alerts">Alertas de Umbral</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Estado
          </label>
          <select
            value={filters?.status}
            onChange={(e) => setFilters(prev => ({ ...prev, status: e?.target?.value }))}
            className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="all">Todos los estados</option>
            <option value="sent">Enviado</option>
            <option value="failed">Fallido</option>
          </select>
        </div>
      </div>
      {/* Timeline View */}
      <div className="space-y-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Icon name="Loader2" size={48} className="animate-spin text-primary" />
          </div>
        ) : logs?.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Icon name="Mail" size={48} className="mx-auto mb-2 opacity-50" />
            <p>No se encontraron registros de email</p>
          </div>
        ) : (
          logs?.map((log) => (
            <div
              key={log?.id}
              className="p-4 bg-background border border-border rounded-lg hover:shadow-elevation-1 transition-smooth"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-start gap-3 flex-1">
                  <div className="p-2 bg-primary/10 rounded-lg mt-1">
                    <Icon
                      name={getReportTypeIcon(log?.reportType)}
                      size={20}
                      className="text-primary"
                    />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-foreground mb-1">
                      {log?.subject}
                    </h3>
                    <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Icon name="User" size={14} />
                        <span>{log?.client?.nombre || 'Cliente Desconocido'}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Icon name="Mail" size={14} />
                        <span>{log?.recipientEmail}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Icon name="Tag" size={14} />
                        <span>{getReportTypeLabel(log?.reportType)}</span>
                      </div>
                    </div>
                  </div>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(log?.status)}`}>
                  {log?.status === 'sent' ? 'Enviado' : 'Fallido'}
                </span>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 pt-4 border-t border-border">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Fecha de Envío</p>
                  <p className="text-sm font-medium text-foreground">
                    {formatDate(log?.sentAt)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Aperturas</p>
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-foreground">
                      {log?.openCount || 0}
                    </p>
                    {log?.openedAt && (
                      <Icon name="Eye" size={14} className="text-green-600" />
                    )}
                  </div>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Clics</p>
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-foreground">
                      {log?.clickCount || 0}
                    </p>
                    {log?.clickedAt && (
                      <Icon name="MousePointerClick" size={14} className="text-purple-600" />
                    )}
                  </div>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Engagement</p>
                  <div className="flex items-center gap-1">
                    {log?.clickedAt ? (
                      <span className="text-sm font-medium text-green-600">Alto</span>
                    ) : log?.openedAt ? (
                      <span className="text-sm font-medium text-yellow-600">Medio</span>
                    ) : (
                      <span className="text-sm font-medium text-gray-600">Bajo</span>
                    )}
                  </div>
                </div>
              </div>

              {log?.errorMessage && (
                <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <Icon name="AlertCircle" size={16} className="text-red-600 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-red-600 mb-1">Error de Entrega</p>
                      <p className="text-xs text-red-600">{log?.errorMessage}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default EmailCommunicationLog;